/**
 * The session's own assurance level — the 2FA half of #1158 M6.
 *
 * ── why this is a claim read and not a door ────────────────────────────────
 * `aal` ("authenticator assurance level") is minted by GoTrue when it BUILDS
 * the access token, alongside `sub` and `role`, and is never copied from a
 * user-supplied object. That property is what makes it safe to read here: a
 * client that lied about its own assurance level would be deceiving nobody but
 * itself, because every server-side decision reads the token the server signed,
 * not this function's return value.
 *
 * So there is deliberately no `core.my_2fa_status()` and no read of
 * `auth.mfa_factors`. A SERVER read is only needed when an ADMINISTRATOR must
 * see other people's enrolment — and "who has 2FA" is a security-posture
 * directory with a different and more sensitive authority model. It must not
 * arrive behind a self-service badge; it needs its own design.
 *
 * ── this is NOT verification, and the badge must not imply it is ───────────
 * Decoding a JWT payload is reading, not verifying — the same caveat
 * `decodeSub` carries in `session.ts`. It is sound for labelling the UI and
 * unsound for anything else. Nothing here gates an action.
 *
 * ── `aal1` is not "no second factor enrolled" ──────────────────────────────
 * It says the CURRENT session was established with one factor. A person may
 * hold a second factor and be in an `aal1` session. The copy in `UserMenu`
 * therefore says "signed in with a single factor" rather than "two-factor is
 * off", because the claim does not support the stronger statement.
 *
 * `undefined` means the token carried no `aal` at all — an older GoTrue, or a
 * token minted by something else. The menu renders NO badge in that case, which
 * is the only honest option: an absent fact is not a negative fact, and a shield
 * telling somebody they are single-factor when they are not is a false statement
 * about their own security.
 */

/** The values GoTrue mints today. Anything else is passed through untouched. */
export type AssuranceLevel = 'aal1' | 'aal2' | (string & {})

/**
 * Read `aal` out of an access token. Returns `undefined` for a malformed token
 * or one that carries no such claim — never a default, for the reason above.
 */
export function assuranceOf(accessToken: string | null | undefined): AssuranceLevel | undefined {
  if (!accessToken) return undefined
  try {
    const payload = accessToken.split('.')[1]
    if (!payload) return undefined
    const claims = JSON.parse(
      atob(payload.replace(/-/g, '+').replace(/_/g, '/')),
    ) as { aal?: unknown }
    return typeof claims.aal === 'string' && claims.aal !== '' ? claims.aal : undefined
  } catch {
    return undefined
  }
}

/** Was a second factor used for THIS session? False is not "not enrolled". */
export function isTwoFactorSession(accessToken: string | null | undefined): boolean {
  return assuranceOf(accessToken) === 'aal2'
}
